import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { Dayjs } from 'dayjs';
import { CloseIcon, CalendarIcon, CalendarInputIcon } from 'components/icons';
import { darkTheme } from '../datePicker/DatePicker';
import { getDayjsLocale, getMuiDatePickerLocaleText } from 'utils/datePickerLocale';
import styles from './dateRangePicker.module.scss';

interface DateRangePickerProps {
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onFromChange: (date: Dayjs | null) => void;
  onToChange: (date: Dayjs | null) => void;
  onClear?: () => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  onClear,
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const [tempFromDate, setTempFromDate] = useState<Dayjs | null>(fromDate);
  const [tempToDate, setTempToDate] = useState<Dayjs | null>(toDate);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
    setTempFromDate(fromDate);
    setTempToDate(toDate);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onFromChange(tempFromDate);
    onToChange(tempToDate);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    handleClose();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFromChange(null);
    onToChange(null);
    if (onClear) onClear();
  };

  const handleLeftCalendarChange = (date: Dayjs | null) => {
    setTempFromDate(date);
    if (date && tempToDate && date.isAfter(tempToDate)) {
      setTempToDate(null);
    }
  };

  const handleRightCalendarChange = (date: Dayjs | null) => {
    setTempToDate(date);
    if (date && tempFromDate && date.isBefore(tempFromDate)) {
      setTempFromDate(date);
      setTempToDate(null);
    }
  };

  const open = !!anchorEl;
  const id = open ? 'date-range-popover' : undefined;

  const formatDate = (date: Dayjs | null) => {
    return date ? date.format('MM/DD/YYYY') : t('datePicker.notSelected');
  };

  return (
    <div className={styles.container}>
      <div className={styles.inputWrapper} onClick={handleClick}>
        <div className={styles.text}>
          {fromDate || toDate ? (
            <>
              <span>{formatDate(fromDate)}</span>
              <span className={styles.separator}>-</span>
              <span>{formatDate(toDate)}</span>
            </>
          ) : (
            <span className={styles.placeholder}>{t('datePicker.label')}</span>
          )}
        </div>
        <div className={styles.iconWrapper}>
          {fromDate || toDate ? (
            <CloseIcon onClick={handleClear} width="18" height="18" className={styles.clearIcon} />
          ) : (
            <CalendarIcon width="20" height="20" className={styles.icon} />
          )}
        </div>
      </div>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: styles.popoverPaper }}
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
      >
        <ThemeProvider theme={darkTheme}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={getDayjsLocale(currentLang)}
            localeText={getMuiDatePickerLocaleText(currentLang)}
          >
            <div className={styles.popoverContainer}>
              <div className={styles.calendarsWrapper}>
                <div className={styles.calendarColumn}>
                  <DateCalendar
                    value={tempFromDate}
                    onChange={handleLeftCalendarChange}
                    sx={{
                      width: { xs: '100%', sm: '280px' },
                      maxWidth: '280px',
                      '& .MuiPickersYear-yearButton': { color: '#ffffff' },
                      '& .MuiPickersMonth-monthButton': { color: '#ffffff' },
                      '& .MuiYearCalendar-root': { width: '100%', color: 'lightgray' },
                      '& .MuiPickersCalendarHeader-label': { color: '#ffffff' },
                      '& .MuiPickersCalendarHeader-labelContainer': { color: '#ffffff' },
                    }}
                    // Optional: we can disable dates after tempToDate
                    // maxDate={tempToDate || undefined}
                  />
                </div>
                <div className={styles.calendarColumn}>
                  <DateCalendar
                    value={tempToDate}
                    onChange={handleRightCalendarChange}
                    sx={{
                      width: { xs: '100%', sm: '280px' },
                      maxWidth: '280px',
                      '& .MuiPickersYear-yearButton': { color: '#ffffff' },
                      '& .MuiPickersMonth-monthButton': { color: '#ffffff' },
                      '& .MuiYearCalendar-root': { width: '100%', color: 'lightgray' },
                      '& .MuiPickersCalendarHeader-label': { color: '#ffffff' },
                      '& .MuiPickersCalendarHeader-labelContainer': { color: '#ffffff' },
                    }}
                    // Optional: we can disable dates before tempFromDate
                    // minDate={tempFromDate || undefined}
                  />
                </div>
              </div>

              <div className={styles.footer}>
                <div className={styles.dateInputs}>
                  <div className={styles.dateBox}>
                    <span>{formatDate(tempFromDate)}</span>
                    <CalendarInputIcon width="14" height="14" />
                  </div>
                  <span className={styles.footerSeparator}>-</span>
                  <div className={styles.dateBox}>
                    <span>{formatDate(tempToDate)}</span>
                    <CalendarInputIcon width="14" height="14" />
                  </div>
                </div>

                <button className={styles.applyBtn} onClick={handleApply}>
                  {t('datePicker.apply')}
                </button>
              </div>
            </div>
          </LocalizationProvider>
        </ThemeProvider>
      </Popover>
    </div>
  );
};

export default DateRangePicker;
